/**
 * DraggableResizableWindow — Fenster-Wrapper mit Steuer-Buttons
 * (Minimieren / Vollbild / Schließen), Drag am Header, Resize rechts unten.
 *
 * Verwendung:
 *   <DraggableResizableWindow open={open} onClose={fn} title="…" storageKey="AccountingPage">
 *     …Inhalt…
 *   </DraggableResizableWindow>
 *
 * States:
 *  - normal:     zentriert (Startposition), Größe 960×700, resizable
 *  - fullscreen: 100vw × 100vh
 *  - minimized:  ruft onMinimize() auf, falls gesetzt (z. B. Fenster nur
 *                ausblenden statt schließen); sonst Fallback auf onClose()
 *                (Zurück zur Übersicht) — bisheriges Verhalten unverändert.
 *
 * initialFullscreen:
 *  - Fenster öffnet im Vollbild; über den Button kann weiterhin verkleinert werden.
 *
 * fillHeight:
 *  - Fenster öffnet in voller Bildschirmhöhe (oben/unten je FILL_MARGIN px
 *    Abstand), solange keine gespeicherte Position/Größe vorliegt. Danach
 *    gelten die vom Nutzer gesetzte Position und Größe.
 *
 * headerVariant="project" + headerContent:
 *  - Kopfzeile im Look des Projekt-Workspace (blauer Verlauf, weiße Steuer-Buttons) mit eigenem
 *    Inhalt statt Titel (z. B. Objekt-/Wohneinheit-Fenster). Interaktive Elemente im headerContent
 *    stoppen das Ziehen selbst (onMouseDown → stopPropagation).
 *  - bodyClassName: Klassen des Inhaltsbereichs (Standard: scrollt selbst).
 *
 * Persistenz:
 *  - Wird ein ``storageKey`` gesetzt, speichert das Fenster Position, Größe und
 *    Vollbild-Zustand im LocalStorage (Key: ``drw:{storageKey}``) und stellt sie
 *    beim nächsten Öffnen wieder her. Ohne Key läuft der Fenster-State flüchtig.
 */
import { useEffect, useRef, useState } from 'react';
import { Minus, Square, X, Copy } from 'lucide-react';

const DEFAULT_W = 960;
const DEFAULT_H = 700;
const MIN_W = 480;
const MIN_H = 320;
const FILL_MARGIN = 8;

const readPersisted = (key) => {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(`drw:${key}`);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (
      typeof data?.x === 'number' &&
      typeof data?.y === 'number' &&
      typeof data?.w === 'number' &&
      typeof data?.h === 'number'
    ) {
      return data;
    }
  } catch (_e) { /* ignore */ }
  return null;
};

// Alte Einträge (vor Speicherung von Vollbild) bei fillHeight-Fenstern: Höhe/Top waren
// damals wirkungslos und nicht vom Nutzer gesetzt → nur x/Breite übernehmen
const readWindowState = (key, fillHeight) => {
  const p = readPersisted(key);
  if (p && fillHeight && typeof p.fs !== 'boolean') {
    return { ...p, y: FILL_MARGIN, h: window.innerHeight - FILL_MARGIN * 2 };
  }
  return p;
};

const persist = (key, state) => {
  if (!key) return;
  try {
    localStorage.setItem(`drw:${key}`, JSON.stringify(state));
  } catch (_e) { /* Quota überschritten oder Storage deaktiviert */ }
};

export default function DraggableResizableWindow({
  open,
  onClose,
  onMinimize = null,
  title,
  testid = 'draggable-window',
  storageKey = null,
  children,
  initialWidth = DEFAULT_W,
  initialHeight = DEFAULT_H,
  initialX = null,
  initialY = null,
  headerActions = null,
  active = true, // false = ausgeblendet (z. B. Tab im Hintergrund) → kein ESC-Schließen
  fillHeight = false, // true = immer volle Bildschirmhöhe
  initialFullscreen = false, // true = öffnet im Vollbild (per Button verkleinerbar)
  zIndex = 50, // z. B. höher, wenn das Fenster über einem anderen Fenster (Composer) liegen muss
  headerVariant = 'default', // 'project' = blaue Kopfzeile wie im Projekt-Workspace
  headerContent = null,      // eigener Kopfzeilen-Inhalt statt `title`
  bodyClassName = 'flex-1 overflow-y-auto',
}) {
  const projektKopf = headerVariant === 'project';
  const btnCls = projektKopf
    ? 'w-7 h-7 rounded-md hover:bg-white/15 text-white/90 hover:text-white flex items-center justify-center transition-colors'
    : 'w-7 h-7 rounded-md hover:bg-white/60 text-gray-600 hover:text-[#1E3A5F] flex items-center justify-center transition-colors';
  const [fullscreen, setFullscreen] = useState(() => {
    const persisted = readPersisted(storageKey);
    return typeof persisted?.fs === 'boolean' ? persisted.fs : initialFullscreen;
  });

  // Auf Mobile (Touch, schmaler Viewport) gibt es kein Drag/Resize per Maus —
  // das Fenster würde sonst in Desktop-Größe (z. B. 1240×820) weit über den
  // Bildschirmrand hinausragen und wäre unbedienbar (Felder/Speichern-Button
  // nicht erreichbar). Dort läuft es immer im Vollbild-Layout.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Handy: solange ein Vollbild-Fenster offen ist, schwebende Knöpfe (z. B. Notizbuch-Stift)
  // ausblenden – sie würden sonst „Weiter“/„Speichern“ im Fensterfuß verdecken (siehe index.css).
  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const b = document.body;
    b.dataset.vollbildFenster = String((Number(b.dataset.vollbildFenster) || 0) + 1);
    return () => {
      const n = (Number(b.dataset.vollbildFenster) || 1) - 1;
      if (n > 0) b.dataset.vollbildFenster = String(n); else delete b.dataset.vollbildFenster;
    };
  }, [open, isMobile]);

  // Initialer State: LocalStorage → Fallback zentriert (bzw. auf explizite initialX/Y)
  const [pos, setPos] = useState(() => {
    const persisted = readWindowState(storageKey, fillHeight);
    if (persisted) {
      return {
        x: Math.max(0, Math.min(persisted.x, window.innerWidth - 100)),
        y: Math.max(0, Math.min(persisted.y, window.innerHeight - 50)),
      };
    }
    const defaultX = initialX != null ? initialX : Math.max(0, (window.innerWidth - initialWidth) / 2);
    const defaultY = initialY != null ? initialY : fillHeight ? FILL_MARGIN : Math.max(0, (window.innerHeight - initialHeight) / 2);
    return { x: defaultX, y: defaultY };
  });
  const [size, setSize] = useState(() => {
    const persisted = readWindowState(storageKey, fillHeight);
    if (persisted) {
      return {
        w: Math.max(MIN_W, Math.min(persisted.w, window.innerWidth)),
        h: Math.max(MIN_H, Math.min(persisted.h, window.innerHeight)),
      };
    }
    return { w: initialWidth, h: fillHeight ? window.innerHeight - FILL_MARGIN * 2 : initialHeight };
  });

  const dragState = useRef(null);
  const resizeState = useRef(null);
  // Aktueller Stand für das Speichern beim Loslassen (vermeidet veraltete Closures)
  const latest = useRef({ pos, size, fullscreen });
  latest.current = { pos, size, fullscreen };

  const save = (override = {}) => {
    const { pos: p, size: sz, fullscreen: fs } = { ...latest.current, ...override };
    persist(storageKey, { x: p.x, y: p.y, w: sz.w, h: sz.h, fs });
  };

  const toggleFullscreen = () => {
    const next = !fullscreen;
    setFullscreen(next);
    save({ fullscreen: next });
  };

  // ESC schließt
  useEffect(() => {
    if (!open || !active) return;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // ESC in einem Eingabefeld (z. B. Auswahlliste zuklappen, Tippfehler verwerfen)
      // darf nicht das ganze Fenster samt Eingaben schließen
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, active, onClose]);

  // Drag/Resize-Handler
  useEffect(() => {
    const onMove = (e) => {
      if (dragState.current) {
        const { startX, startY, origX, origY } = dragState.current;
        setPos({
          x: Math.max(0, Math.min(window.innerWidth - 100, origX + (e.clientX - startX))),
          y: Math.max(0, Math.min(window.innerHeight - 50, origY + (e.clientY - startY))),
        });
      }
      if (resizeState.current) {
        const { startX, startY, origW, origH } = resizeState.current;
        setSize({
          w: Math.max(MIN_W, origW + (e.clientX - startX)),
          h: Math.max(MIN_H, origH + (e.clientY - startY)),
        });
      }
    };
    const onUp = () => {
      const wasDragging = !!dragState.current;
      const wasResizing = !!resizeState.current;
      dragState.current = null;
      resizeState.current = null;
      document.body.style.userSelect = '';
      // Nach Interaktion persistieren (Position + Größe + Vollbild-Zustand)
      if (wasDragging || wasResizing) save();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const style = fullscreen || isMobile
    // Handy/Vollbild: Kopf nicht unter Notch/Statusleiste, Fußleiste („Speichern“) nicht unter der Home-Leiste
    ? { top: 0, left: 0, width: '100vw', height: '100dvh', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)', paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }
    // Nie über den unteren Bildschirmrand hinausragen (z. B. Fenster in voller Höhe, das nach unten verschoben wurde)
    : { top: pos.y, left: pos.x, width: size.w, height: Math.max(MIN_H, Math.min(size.h, window.innerHeight - pos.y)) };

  const startDrag = (e) => {
    if (fullscreen || isMobile) return;
    dragState.current = {
      startX: e.clientX, startY: e.clientY,
      origX: pos.x, origY: pos.y,
    };
    document.body.style.userSelect = 'none';
  };

  const startResize = (e) => {
    if (fullscreen || isMobile) return;
    e.stopPropagation();
    resizeState.current = {
      startX: e.clientX, startY: e.clientY,
      origW: size.w, origH: size.h,
    };
    document.body.style.userSelect = 'none';
  };

  return (
    <>
      {/* dünner Backdrop für Fokus, aber Interaktion mit Rest bleibt möglich (pointer-events: none) */}
      <div className="fixed inset-0 bg-black/10" style={{ pointerEvents: 'none', zIndex: zIndex - 10 }} />
      <div
        className="fixed bg-white shadow-2xl rounded-xl overflow-hidden flex flex-col border border-gray-200"
        // margin: 0 – sonst vergibt ein Eltern-Container mit space-y-* dem fixierten Fenster einen
        // margin-top, und es ragt unten über den Bildschirmrand (Fußleiste mit „Weiter“/„Speichern“ abgeschnitten).
        style={{ ...style, margin: 0, zIndex, pointerEvents: 'auto' }}
        data-testid={testid}
      >
        {/* Header: Titel + Window-Controls */}
        <div
          className={`flex items-center gap-2 px-4 py-2 select-none ${projektKopf ? 'text-white' : 'bg-blue-50 border-b border-blue-100'} ${isMobile ? '' : 'cursor-move'}`}
          style={projektKopf ? { background: 'var(--ew-nav-bg)' } : undefined}
          onMouseDown={startDrag}
          data-testid={`${testid}-header`}
        >
          {headerContent
            ? <div className="flex-1 min-w-0">{headerContent}</div>
            : <div className={`flex-1 text-sm font-semibold truncate ${projektKopf ? 'text-white' : 'text-[#1E3A5F]'}`}>{title}</div>}
          {headerActions && (
            <div className="flex items-center gap-2 mr-1" onMouseDown={(e) => e.stopPropagation()}>
              {headerActions}
            </div>
          )}
          <button
            type="button"
            onClick={onMinimize || onClose}
            onMouseDown={(e) => e.stopPropagation()}
            title={onMinimize ? 'Minimieren' : 'Minimieren (zurück zur Übersicht)'}
            data-testid={`${testid}-minimize`}
            className={btnCls}>
            <Minus className="w-4 h-4" />
          </button>
          {!isMobile && (
            <button
              type="button"
              onClick={toggleFullscreen}
              onMouseDown={(e) => e.stopPropagation()}
              title={fullscreen ? 'Verkleinern' : 'Vollbild'}
              data-testid={`${testid}-toggle-fullscreen`}
              className={btnCls}>
              {fullscreen ? <Copy className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
            title="Schließen"
            data-testid={`${testid}-close`}
            className={projektKopf ? btnCls : 'w-7 h-7 rounded-md hover:bg-red-100 text-gray-600 hover:text-red-600 flex items-center justify-center transition-colors'}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Inhalt */}
        <div className={bodyClassName} data-testid={`${testid}-body`}>
          {children}
        </div>

        {/* Resize-Handle rechts unten */}
        {!fullscreen && !isMobile && (
          <div
            onMouseDown={startResize}
            title="Größe ändern"
            data-testid={`${testid}-resize`}
            className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize"
            style={{
              backgroundImage:
                'linear-gradient(135deg, transparent 45%, #94a3b8 45%, #94a3b8 55%, transparent 55%, transparent 65%, #94a3b8 65%, #94a3b8 75%, transparent 75%)',
            }}
          />
        )}
      </div>
    </>
  );
}
